import * as React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { conditionRegistry, ConditionDefinition } from '../../builder/config/conditionRegistry';
import { CanonicalEventName } from '@/gql/graphql';

// Define Activity type based on what we generate
export interface Activity {
  id: CanonicalEventName;
  name: string; // This will be the ConditionDefinition label
}

interface SelectActivityStepProps {
  onBack: () => void;
  onAddConditions: (selectedActivities: Activity[]) => void;
}

// Generate the list of activities directly from the registry
const AVAILABLE_ACTIVITIES: Activity[] = Object.values(conditionRegistry)
  // Type guard to filter out undefined entries and ensure correct type
  .filter(
    (def: ConditionDefinition | undefined): def is ConditionDefinition =>
      !!def && def.type === 'event'
  )
  .map((def) => ({
    id: def.id as CanonicalEventName,
    name: def.label
  }));

export function SelectActivityStep({ onBack, onAddConditions }: SelectActivityStepProps) {
  const [selected, setSelected] = React.useState<Activity[]>([]);

  const handleSelect = (activity: Activity) => {
    setSelected((prev) =>
      prev.find((a) => a.id === activity.id)
        ? prev.filter((a) => a.id !== activity.id)
        : [...prev, activity]
    );
  };

  const handleAddClick = () => {
    onAddConditions(selected);
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center">
          <Button variant="ghost" size="icon" className="mr-2 h-7 w-7" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <DialogTitle>Choose a Subscriber Activity</DialogTitle>
        </div>
      </DialogHeader>
      <div className="py-4 max-h-[400px] overflow-y-auto pr-2">
        {AVAILABLE_ACTIVITIES.map((activity) => (
          <Button
            key={activity.id}
            variant="ghost"
            className="w-full justify-start h-auto py-3 px-3 hover:bg-accent/50 data-[selected=true]:bg-accent/60 border-b rounded-none"
            data-selected={selected.some((a) => a.id === activity.id)}
            onClick={() => {
              handleSelect(activity);
            }}
          >
            <span className="font-normal text-sm">{activity.name}</span>
          </Button>
        ))}
      </div>
      <DialogFooter className="mt-4">
        <span className="text-sm text-muted-foreground mr-auto">{selected.length} selected</span>
        <Button type="button" disabled={selected.length === 0} onClick={handleAddClick}>
          Add conditions
        </Button>
      </DialogFooter>
    </>
  );
}
